import type { ReactElement } from 'react';
import { GroupedDataGrid, type GroupedColumnDef } from '../grid/GroupedDataGrid';
import { GridPaginationBar } from './GridPaginationBar';

export type MaterialTransferGridRow = {
  id: string;
  transferNumber: string;
  status: string;
  region: string;
  movementDateUtc: string;
  lines: { inventoryItemId: string; sku: string; lineCondition: string }[];
};

const MT_COLUMNS: GroupedColumnDef[] = [
  { id: 'transferNumber', label: 'Number', header: 'Number' },
  { id: 'status', label: 'Status', header: 'Status' },
  { id: 'region', label: 'Region', header: 'Region' },
  { id: 'movementDate', label: 'Move date (UTC)', header: 'Move date (UTC)' },
  { id: 'lines', label: 'Lines', header: 'Lines' },
  { id: 'actions', label: 'Actions', header: 'Actions' }
];

function formatTransferStatusLabel(status: string): string {
  const s = (status ?? '').trim();
  if (!s) {
    return '—';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function statusPillClass(status: string): string {
  const s = (status ?? '').toLowerCase().replace(/\s+/g, '');
  if (!s) {
    return 'status-pill';
  }
  return `status-pill mt-status-${s}`;
}

function linesSummaryForGroup(row: MaterialTransferGridRow): string {
  if (!row.lines?.length) {
    return '—';
  }
  return row.lines.map((l) => `${l.sku} (${l.lineCondition})`).join(', ');
}

function getGroupValue(row: MaterialTransferGridRow, columnId: string): string {
  switch (columnId) {
    case 'transferNumber':
      return row.transferNumber ?? '—';
    case 'status':
      return formatTransferStatusLabel(row.status);
    case 'region':
      return row.region ?? '—';
    case 'movementDate':
      return row.movementDateUtc?.replace('T', ' ').slice(0, 19) ?? '—';
    case 'lines':
      return linesSummaryForGroup(row);
    case 'actions':
      return '—';
    default:
      return '—';
  }
}

export type MaterialTransferListStatusFilter =
  | 'all'
  | 'draft'
  | 'submitted'
  | 'planned'
  | 'completed'
  | 'approved'
  | 'rejected'
  | 'cancelled';

export interface MaterialTransfersGridProps {
  error?: string | null;
  rows: MaterialTransferGridRow[];
  loading: boolean;
  selectedId: string | null;
  onSelectRow: (id: string) => void;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  statusFilter: MaterialTransferListStatusFilter;
  onStatusFilterChange: (filter: MaterialTransferListStatusFilter) => void;
  onNewTransfer: () => void;
  /** Open / double-click — full form edit. */
  onOpenTransferEdit: (id: string) => void;
}

export function MaterialTransfersGrid({
  error,
  rows,
  loading,
  selectedId,
  onSelectRow,
  page: currentPage,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
  statusFilter,
  onStatusFilterChange,
  onNewTransfer,
  onOpenTransferEdit
}: MaterialTransfersGridProps): ReactElement {
  function renderRow(row: MaterialTransferGridRow): ReactElement {
    const st = (row.status ?? '').trim();
    return (
      <tr
        className={selectedId === row.id ? 'selected' : undefined}
        onClick={() => onSelectRow(row.id)}
        onDoubleClick={(e) => {
          e.preventDefault();
          onOpenTransferEdit(row.id);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectRow(row.id);
          }
        }}
        role="button"
        tabIndex={0}
      >
        <td className="mono-id">{row.transferNumber}</td>
        <td>
          <span className={statusPillClass(st)}>{formatTransferStatusLabel(st)}</span>
        </td>
        <td>{row.region}</td>
        <td>{row.movementDateUtc?.replace('T', ' ').slice(0, 19)}</td>
        <td>
          {row.lines?.length ? (
            <ul className="mt-transfer-line-list">
              {row.lines.map((l) => (
                <li key={l.inventoryItemId + l.lineCondition}>
                  <span className="mono-id">{l.sku}</span> — {l.lineCondition}
                </li>
              ))}
            </ul>
          ) : (
            '—'
          )}
        </td>
        <td
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="secondary"
            onClick={() => onOpenTransferEdit(row.id)}
          >
            Open
          </button>
        </td>
      </tr>
    );
  }

  return (
    <section className="card requests-grid-card">
      <div className="row spread requests-grid-toolbar">
        <h2>Material Transfer</h2>
        <button
          type="button"
          className="secondary well-add-btn"
          title="New Material Transfer"
          aria-label="New Material Transfer"
          onClick={(e) => {
            e.stopPropagation();
            onNewTransfer();
          }}
        >
          +
        </button>
      </div>
      <p className="hint">
        Draft a transfer, submit for review (requestors), then procurement approves to <strong>Planned</strong>. Attach a
        movement ticket to complete it and move inventory. <strong>Click a row</strong> for the side panel;{' '}
        <strong>double-click</strong> or use <strong>Open</strong> to edit the full form. Sort, filter, and group like
        other grids.
      </p>
      {error && <p className="danger-text">{error}</p>}
      <div className="row filter-row">
        <label htmlFor="mt-status-filter">Status</label>
        <select
          id="mt-status-filter"
          value={statusFilter}
          onChange={(e) => onStatusFilterChange(e.target.value as MaterialTransferListStatusFilter)}
        >
          <option value="all">All statuses</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
          <option value="planned">Planned</option>
          <option value="completed">Completed</option>
          <option value="approved">Approved (legacy)</option>
          <option value="rejected">Rejected</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>
      {loading && <p className="hint">Loading Material Transfer list…</p>}
      <GroupedDataGrid<MaterialTransferGridRow>
        columns={MT_COLUMNS}
        rows={rows}
        getRowId={(r) => r.id}
        getGroupValue={getGroupValue}
        renderRow={renderRow}
        colSpan={6}
        emptyMessage="No Material Transfers in your regions (or no matches for this status)."
        groupHint="Click a column title to sort (this page). Drag the grip (⠿) beside a title into the strip to group.
          Column filters search within the loaded page only."
      />
      <GridPaginationBar
        idPrefix="material-transfers-grid"
        page={currentPage}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </section>
  );
}
